import * as React from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cn } from "./utils";

const CustomSwitchWithMore = React.forwardRef(
    (
        {
            checked,
            className,
            autoFocus = false,
            checkedChildren,
            defaultChecked = false,
            disabled = false,
            loading = false,
            size = "default",
            unCheckedChildren,
            label,
            labelPosition = "left",
            onCheckedChange = () => {},
            ...props
        },
        ref
    ) => {
        const [thumbWidth, setThumbWidth] = React.useState(0);
        const [isChecked, setIsChecked] = React.useState(defaultChecked);

        // 动态计算 checkedChildren 和 unCheckedChildren 的宽度
        React.useEffect(() => {
            const getTextWidth = (text) => {
                if (!text) return 0;
                const element = document.createElement("span");
                element.style.visibility = "hidden";
                element.style.position = "absolute";
                element.style.whiteSpace = "nowrap";
                element.innerText = text;

                document.body.appendChild(element);
                const width = element.offsetWidth;
                document.body.removeChild(element);

                return width;
            };

            const checkedWidth = getTextWidth(checkedChildren);
            const uncheckedWidth = getTextWidth(unCheckedChildren);

            // 确保按钮的宽度至少是高度的两倍，以保持圆形
            const maxTextWidth = Math.max(checkedWidth, uncheckedWidth);
            setThumbWidth(maxTextWidth + 8); // 添加 padding 使文字不贴边
        }, [checkedChildren, unCheckedChildren]);

        const switchHeight = size === "small" ? 24 : 32; // 小尺寸和默认尺寸
        const switchWidth = thumbWidth + switchHeight; // 按钮宽度 + 开关高度

        // 根据 data-state 判断开关状态
        const isSwitchChecked =
            className?.includes("data-[state=checked]") || isChecked;

        const handleSwitchChange = () => {
            if (!disabled) {
                setIsChecked((prev) => {
                    onCheckedChange(!prev);
                    return !prev;
                });
            }
        };
        React.useEffect(() => {
            setIsChecked(checked)
        }, [checked]);
        return (
            <div className="flex items-center">
                {label && labelPosition === "left" && (
                    <span className="mr-2 text-sm">{label}</span>
                )}
                <SwitchPrimitives.Root
                    className={cn(
                        "relative inline-flex items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
                        isSwitchChecked ? "bg-primary" : "bg-input",
                        className
                    )}
                    ref={ref}
                    checked={isSwitchChecked} // 根据 className 判断开关状态
                    defaultChecked={defaultChecked}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    onClick={handleSwitchChange}
                    {...props}
                    style={{
                        width: `${switchWidth}px`,
                        height: `${switchHeight}px`,
                    }} // 设置 Switch 的宽度和高度
                >
                    <span
                        className={cn(
                            "absolute inset-0 flex items-center text-xs font-medium text-white transition-transform",
                            isSwitchChecked
                                ? "justify-start pl-2"
                                : "justify-end pr-2"
                        )}
                    >
                        {isSwitchChecked ? checkedChildren : unCheckedChildren}
                    </span>
                    <SwitchPrimitives.Thumb
                        className={cn(
                            "pointer-events-none block bg-background shadow-lg ring-0 transition-transform rounded-full"
                        )}
                        style={{
                            transform: isSwitchChecked
                                ? `translateX(${thumbWidth + 4}px)`
                                : "translateX(4px)",
                            width: `${size === "small" ? 18 : 24}px`,
                            height: `${size === "small" ? 18 : 24}px`,
                        }}
                    />
                </SwitchPrimitives.Root>
                {label && labelPosition === "right" && (
                    <span className="ml-2 text-sm">{label}</span>
                )}
            </div>
        );
    }
);

CustomSwitchWithMore.displayName = SwitchPrimitives.Root.displayName;

export { CustomSwitchWithMore };
